import React from 'react';
import PropTypes from 'prop-types';

import {
  Criterium,
  CriteriumTable,
  CriteriumChart,
  CriteriumSkeleton
} from '@hydra/common/criterium';
import { Translate, SafeAnchor } from '@hydra/common/components';
import { useReviewContext, usePdfTemplateContext } from '@hydra/common/context';

import { getContrastedColor, getPriSecColors } from '@hydra/utils/colors';
import { useI18n } from '@hydra/common/customHooks';

export const LinksDetails = ({ criterium }) => {
  const { translate } = useI18n();
  const { isPdf, variant } = useReviewContext();
  const { templateColors } = usePdfTemplateContext();

  const { primary } = isPdf
    ? templateColors
    : getPriSecColors();

  const color = getContrastedColor(primary);
  const { data } = criterium;

  const linksToFiles = data.filter(link => link.linkToFile);
  const internalLinks = data.filter(link => link.internal);
  const externalFollowLinks = data.filter(link => !link.internal && link.follow);
  const externalNoFollowLinks = data.filter(link => !link.internal && !link.follow);

  const chartData = {
    name: 'links_details',
    data: [
      {
        name: translate('internal'),
        value: internalLinks.length
      },
      {
        name: translate('follow_links'),
        value: externalFollowLinks.length
      },
      {
        name: translate('nofollow_links'),
        value: externalNoFollowLinks.length
      }
    ]
  };
  const chartProps = {
    data: chartData
  };
  if (criterium.chartOptions) {
    chartProps.chartOptions = criterium.chartOptions;
  }

  const head = [
    <Translate content='table_linktag' key='table_linktag' />,
    <Translate content='table_internal' key='table_internal' />,
    <Translate content='table_follow' key='table_follow' />
  ];

  const teaser = data
    .map(link => ([
      <SafeAnchor href={link.link} rel='nofollow noopener noreferrer' target='_blank' key={link.link}>{link.hypertext || translate('no_anchor_text')}</SafeAnchor>,
      <Translate content={link.internal ? 'internal' : 'external'} key={link.internal ? 'internal' : 'external'} />,
      <Translate content={link.follow ? 'follow' : 'nofollow'} key={link.follow ? 'follow' : 'nofollow'} />
    ]));

  const content = (
    <>
      {
        variant === 'nocrit'
          ? <CriteriumSkeleton width='60%' />
          : <Translate
              content='link_details'
              with={{ linksCount: data.length, linksToFilesCount: linksToFiles.length }}
            />
      }
      {data.length > 0
        ? (
          <div>
            {variant === 'nocrit' ? <CriteriumSkeleton variant='circle' width='30%' style={{ height: '200px', borderRadius: '50%', margin: '25px' }} /> : <CriteriumChart type='pie' {...chartProps} />}
            <CriteriumTable head={head} rows={teaser} color={color} striped nowrap />
          </div>
          )
        : null}
    </>
  );

  return <Criterium criterium={criterium}>{content}</Criterium>;
};

LinksDetails.propTypes = {
  criterium: PropTypes.shape({
    data: PropTypes.arrayOf(
      PropTypes.shape({
        link: PropTypes.string,
        hypertext: PropTypes.string,
        linkToFile: PropTypes.bool,
        follow: PropTypes.bool,
        internal: PropTypes.bool
      })
    )
  }),
  color: PropTypes.string
};

export default React.memo(LinksDetails);
